import type { TFunction } from 'i18next';

export interface QuickReply {
  id: string;
  slug: string;
  text: string;
  builtin?: boolean;
}

const USER_STORAGE_KEY = 'ote-user-quick-replies';

export function getSystemQuickReplies(t: TFunction): QuickReply[] {
  return [
    { id: 'screenshot', slug: 'screenshot', text: t('quickReplies.screenshot'), builtin: true },
    { id: 'link', slug: 'link', text: t('quickReplies.link'), builtin: true },
    { id: 'done', slug: 'done', text: t('quickReplies.done'), builtin: true },
    { id: 'thanks', slug: 'thanks', text: t('quickReplies.thanks'), builtin: true },
    { id: 'checking', slug: 'checking', text: t('quickReplies.checking'), builtin: true },
    { id: 'remote', slug: 'remote', text: t('quickReplies.remote'), builtin: true },
    { id: 'details', slug: 'details', text: t('quickReplies.details'), builtin: true },
    { id: 'call', slug: 'call', text: t('quickReplies.call'), builtin: true },
    { id: 'waiting', slug: 'waiting', text: t('quickReplies.waiting'), builtin: true },
    { id: 'received', slug: 'received', text: t('quickReplies.received'), builtin: true },
    { id: 'schedule', slug: 'schedule', text: t('quickReplies.schedule'), builtin: true },
    { id: 'update', slug: 'update', text: t('quickReplies.update'), builtin: true },
  ];
}

export function getUserQuickReplies(): QuickReply[] {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as QuickReply[];
    return Array.isArray(parsed) ? parsed.filter((r) => r.id && r.text) : [];
  } catch {
    return [];
  }
}

export function saveUserQuickReplies(replies: QuickReply[]): void {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(replies));
}

export function addUserQuickReply(text: string): QuickReply {
  const slug = text.slice(0, 20).replace(/\s+/g, '-').toLowerCase() || `custom-${Date.now()}`;
  const reply: QuickReply = { id: `user-${Date.now()}`, slug, text: text.trim() };
  const existing = getUserQuickReplies();
  saveUserQuickReplies([...existing, reply]);
  return reply;
}

export function removeUserQuickReply(id: string): void {
  saveUserQuickReplies(getUserQuickReplies().filter((r) => r.id !== id));
}

export function getAllQuickReplies(t: TFunction): QuickReply[] {
  return [...getSystemQuickReplies(t), ...getUserQuickReplies()];
}

/** Match slash command at end of text: "/screen" or "/screenshot" */
export function matchSlashQuery(text: string): string | null {
  const match = text.match(/(?:^|\s)\/(\w*)$/);
  return match ? match[1].toLowerCase() : null;
}

export function filterQuickReplies(replies: QuickReply[], query: string): QuickReply[] {
  if (!query) return replies;
  return replies.filter(
    (r) => r.slug.startsWith(query) || r.text.includes(query)
  );
}

export function applyQuickReply(text: string, reply: QuickReply): string {
  const replaced = text.replace(/(?:^|\s)\/\w*$/, '');
  const prefix = replaced.trimEnd();
  return prefix ? `${prefix} ${reply.text}` : reply.text;
}
